import { isValidCnpj, isValidCpf } from './commissionPaymentValidation';
import { referenceDateComplete as dateComplete } from '../components/postSaleDate';
import { applySalesPartyPhoneError } from './salesPartyPhoneValidation';

const present = value => String(value || '').trim().length > 0;
const selected = value => present(value) && String(value) !== '0';
const enabled = value => [true, 'true', '1', 'S'].includes(value);
const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const invalidEmail = value =>
  String(value || '').length > 0 && !emailPattern.test(value);
const personFields =
  'cpf birth_date gender name mother_name nationality phone profession marital_status'.split(
    ' '
  );

const required = (source, fields, message) =>
  fields.reduce((errors, field) => {
    if (!present(source?.[field])) errors[field] = message;
    return errors;
  }, {});

const personErrors = (profile, t, isRepresentative) => {
  const identity = profile.identity || {};
  const errors = required(
    identity,
    personFields,
    t('SALES_PARTIES.FORM.REQUIRED')
  );
  if (present(identity.cpf) && !isValidCpf(identity.cpf)) {
    errors.cpf = t('SALES_PARTIES.FORM.CPF_INVALID');
  }
  applySalesPartyPhoneError(identity, errors, t);
  if (present(identity.birth_date) && !dateComplete(identity.birth_date)) {
    errors.birth_date = t('POST_SALE.FORM.VALIDATION.INVALID_DATE');
  }
  if (invalidEmail(identity.email)) {
    errors.email = t('SALES_PARTIES.FORM.EMAIL_INVALID');
  }

  if (identity.marital_status === '1') {
    Object.assign(
      errors,
      required(
        identity,
        ['marital_property_regime'],
        t('SALES_PARTIES.FORM.REQUIRED')
      )
    );
  }

  const needsSpouse =
    identity.marital_status === '1' || enabled(identity.stable_union);
  if (needsSpouse) {
    Object.assign(
      errors,
      required(identity, ['spouse_name'], t('SALES_PARTIES.FORM.REQUIRED'))
    );
    if (present(identity.spouse_cpf) && !isValidCpf(identity.spouse_cpf)) {
      errors.spouse_cpf = t('SALES_PARTIES.FORM.CPF_INVALID');
    }
  }

  if (String(identity.rg || '').trim().length >= 4) {
    Object.assign(
      errors,
      required(
        identity,
        ['rg_issuer', 'rg_issued_on'],
        t('SALES_PARTIES.FORM.REQUIRED')
      )
    );
  }

  if (
    isRepresentative &&
    !identity.is_partner &&
    !identity.is_legal_representative
  ) {
    errors.representative_role = t(
      'SALES_PARTIES.FORM.REPRESENTATIVE_ROLE_REQUIRED'
    );
  }

  return errors;
};

const companyErrors = (profile, t) => {
  const identity = profile.identity || {};
  const errors = required(
    identity,
    ['cnpj', 'phone', 'legal_name', 'trade_name'],
    t('SALES_PARTIES.FORM.REQUIRED')
  );
  if (present(identity.cnpj) && !isValidCnpj(identity.cnpj)) {
    errors.cnpj = t('SALES_PARTIES.FORM.CNPJ_INVALID');
  }
  applySalesPartyPhoneError(identity, errors, t);
  return errors;
};

export const sectionErrors = ({ profile, party, section, t }) => {
  if (section === 'personal') {
    return personErrors(profile, t, party?.relation_type === 'representante');
  }

  if (section === 'company') {
    return companyErrors(profile, t);
  }

  if (section === 'address') {
    return required(
      profile.address,
      ['cep', 'street', 'number', 'city', 'state', 'neighborhood'],
      t('SALES_PARTIES.FORM.REQUIRED')
    );
  }

  if (section === 'attorney' && profile.attorney?.enabled) {
    const errors = required(
      profile.attorney,
      ['name', 'phone'],
      t('SALES_PARTIES.FORM.REQUIRED')
    );
    return applySalesPartyPhoneError(profile.attorney, errors, t);
  }

  if (
    section === 'encumbrances' &&
    profile.encumbrances_enabled &&
    !profile.encumbrances?.length
  ) {
    return { encumbrances: t('SALES_PARTIES.ENCUMBRANCES.REQUIRED') };
  }

  if (section === 'origin') {
    return ['channel', 'contact_method', 'contacted_on'].reduce(
      (errors, field) => {
        const value = profile.origin?.[field];
        if (!(field === 'contacted_on' ? present(value) : selected(value))) {
          errors[field] = t('SALES_PARTIES.FORM.REQUIRED');
        }
        return errors;
      },
      {}
    );
  }

  if (section === 'documents' && profile.documents?.complete !== true) {
    return { documents: t('SALES_PARTIES.DOCUMENTS.REQUIRED') };
  }

  return {};
};

export const allSectionErrors = ({ profile, party, sections, t }) =>
  sections.reduce((errors, section) => {
    const sectionError = sectionErrors({ profile, party, section, t });
    if (Object.keys(sectionError).length) errors[section] = sectionError;
    return errors;
  }, {});
